/**
 * BodyCompositionEditorDialog — Phase 2.2B Session 2
 *
 * Endpoint: POST /api/nutrition/body-composition
 * Backend computes body_fat_pct_final / lean_mass_kg_final / fat_mass_kg_final
 * server-side based on method + protocol.
 *
 * Supported methods:
 *   • SKINFOLDS — guided (JP3 / JP7 / DURNIN_WOMERSLEY) OR custom (free site list
 *                 + mandatory manual BF% override; backend stores custom_sites).
 *   • BIA — Tanita/InBody payload (fm/ffm/tbw/icw/ecw/phase_angle/smm)
 *   • DEXA — gold standard (fm/ffm/bone_mineral)
 *   • MANUAL — nutritionist enters BF/fat/lean directly
 *
 * RBAC: ADMIN + NUTRITIONIST (gated upstream).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Loader2, Save, Trash2, Beaker } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';

const METHODS = ['SKINFOLDS', 'BIA', 'DEXA', 'MANUAL'];
const SKINFOLD_PROTOCOLS = [
  { id: 'JP3', label: 'Jackson-Pollock 3' },
  { id: 'JP7', label: 'Jackson-Pollock 7' },
  { id: 'DURNIN_WOMERSLEY', label: 'Durnin-Womersley' },
  { id: 'CUSTOM', label: 'Custom' },
];

// All 9 fixed sites used by the guided protocols. Backend `SkinfoldSites` schema.
const SKINFOLD_SITES = [
  'triceps', 'subscapular', 'suprailiac', 'abdominal',
  'thigh', 'chest', 'midaxillary', 'biceps', 'calf',
];

const PROTOCOL_REQUIRED_SITES = {
  JP3: { M: ['chest', 'abdominal', 'thigh'], F: ['triceps', 'suprailiac', 'thigh'] },
  JP7: { M: ['chest', 'midaxillary', 'triceps', 'subscapular', 'abdominal', 'suprailiac', 'thigh'],
         F: ['chest', 'midaxillary', 'triceps', 'subscapular', 'abdominal', 'suprailiac', 'thigh'] },
  DURNIN_WOMERSLEY: { M: ['biceps', 'triceps', 'subscapular', 'suprailiac'],
                     F: ['biceps', 'triceps', 'subscapular', 'suprailiac'] },
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const toNum = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));

const Field = ({ label, value, onChange, testid, placeholder, suffix, required = false, step = '0.1', min, max }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1 block">
      {label}{required && <span className="text-red-400 ml-1">*</span>}
    </label>
    <div className="relative">
      <input
        type="number" step={step} min={min} max={max}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-600 pr-12"
        data-testid={testid}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 pointer-events-none">{suffix}</span>}
    </div>
  </div>
);

const BodyCompositionEditorDialog = ({ open, onClose, athleteId, athleteName, defaults, onSaved }) => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const [date, setDate] = useState(todayIso());
  const [sex, setSex] = useState('M');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [method, setMethod] = useState('SKINFOLDS');
  const [protocol, setProtocol] = useState('JP3');
  const [skinfolds, setSkinfolds] = useState({});
  const [customSites, setCustomSites] = useState([]);
  const [customDraftName, setCustomDraftName] = useState('');
  const [customDraftValue, setCustomDraftValue] = useState('');
  // method-specific payloads
  const [bia, setBia] = useState({ fm_kg: '', ffm_kg: '', tbw_l: '', icw_l: '', ecw_l: '', smm_kg: '', phase_angle_deg: '' });
  const [dexa, setDexa] = useState({ fm_kg: '', ffm_kg: '', bone_mineral_kg: '' });
  const [manual, setManual] = useState({ body_fat_pct: '', lean_mass_kg: '', fat_mass_kg: '' });
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset when opened
  useEffect(() => {
    if (open) {
      setDate(todayIso());
      setSex(defaults?.sex || 'M');
      setAge(defaults?.age_years ?? '');
      setWeight(defaults?.weight_kg ?? '');
      setMethod('SKINFOLDS');
      setProtocol('JP3');
      setSkinfolds({});
      setCustomSites([]);
      setCustomDraftName(''); setCustomDraftValue('');
      setBia({ fm_kg: '', ffm_kg: '', tbw_l: '', icw_l: '', ecw_l: '', smm_kg: '', phase_angle_deg: '' });
      setDexa({ fm_kg: '', ffm_kg: '', bone_mineral_kg: '' });
      setManual({ body_fat_pct: '', lean_mass_kg: '', fat_mass_kg: '' });
      setNotes('');
      setError(null);
    }
  }, [open, defaults]);

  const requiredSkinfoldSites = useMemo(() => {
    if (method !== 'SKINFOLDS') return [];
    return PROTOCOL_REQUIRED_SITES[protocol]?.[sex] || [];
  }, [method, protocol, sex]);

  if (!open) return null;

  const addCustomSite = () => {
    const n = customDraftName.trim();
    const v = parseFloat(customDraftValue);
    if (!n || isNaN(v)) return;
    setCustomSites([...customSites, { name: n, value_mm: v }]);
    setCustomDraftName(''); setCustomDraftValue('');
  };
  const removeCustomSite = (idx) => setCustomSites(customSites.filter((_, i) => i !== idx));

  const buildPayload = () => {
    const base = {
      athlete_id: athleteId,
      date,
      sex,
      age_years: parseFloat(age),
      weight_kg: parseFloat(weight),
      method,
      protocol: method === 'SKINFOLDS' ? protocol : 'NA',
    };
    if (notes.trim()) base.notes = notes.trim();

    if (method === 'SKINFOLDS') {
      // Send only the sites that have a numeric value
      const sf = {};
      SKINFOLD_SITES.forEach((s) => {
        const v = toNum(skinfolds[s]);
        if (v !== null) sf[s] = v;
      });
      if (Object.keys(sf).length > 0) base.skinfolds = sf;
      if (protocol === 'CUSTOM') {
        if (customSites.length > 0) base.custom_sites = customSites;
        const bf = toNum(manual.body_fat_pct);
        if (bf !== null) base.manual = { body_fat_pct: bf };
      }
    } else if (method === 'BIA') {
      const payload = {};
      Object.entries(bia).forEach(([k, v]) => {
        const n = toNum(v);
        if (n !== null) payload[k] = n;
      });
      base.bia = payload;
    } else if (method === 'DEXA') {
      const payload = {};
      Object.entries(dexa).forEach(([k, v]) => {
        const n = toNum(v);
        if (n !== null) payload[k] = n;
      });
      base.dexa = payload;
    } else if (method === 'MANUAL') {
      const payload = {};
      Object.entries(manual).forEach(([k, v]) => {
        const n = toNum(v);
        if (n !== null) payload[k] = n;
      });
      base.manual = payload;
    }
    return base;
  };

  const handleSave = async () => {
    setError(null);
    // Lightweight client-side checks (server still validates).
    if (!age || !weight) {
      setError(language === 'IT' ? 'Età e peso sono obbligatori' : 'Age and weight are required');
      return;
    }
    if (method === 'SKINFOLDS' && protocol === 'CUSTOM' && !toNum(manual.body_fat_pct)) {
      setError(language === 'IT'
        ? 'Per il protocollo Custom è obbligatorio inserire il BF% manuale'
        : 'Custom skinfold protocol requires manual BF%');
      return;
    }
    if (method === 'SKINFOLDS' && protocol !== 'CUSTOM') {
      const missing = requiredSkinfoldSites.filter((s) => toNum(skinfolds[s]) === null);
      if (missing.length > 0) {
        setError((language === 'IT' ? 'Pliche mancanti: ' : 'Missing sites: ') + missing.join(', '));
        return;
      }
    }
    setSaving(true);
    try {
      const res = await api.post('/nutrition/body-composition', buildPayload());
      onSaved && onSaved(res.data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(Array.isArray(detail) ? detail.map((d) => d.msg).join('; ') : (detail || (language === 'IT' ? 'Salvataggio fallito' : 'Save failed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="bodycomp-editor-dialog">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto m-4">
        <header className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Beaker className="w-5 h-5 text-emerald-400" />
            <div>
              <h2 className="text-lg font-semibold text-zinc-100">
                {language === 'IT' ? 'Nuova composizione corporea' : 'New body composition'}
              </h2>
              <p className="text-xs text-zinc-500 mt-0.5">{athleteName}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="bodycomp-editor-close">
            <X className="w-4 h-4" />
          </Button>
        </header>

        <div className="p-6 space-y-5">
          {/* Common header fields */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1 block">
                {language === 'IT' ? 'Data' : 'Date'}<span className="text-red-400 ml-1">*</span>
              </label>
              <input
                type="date" value={date} max={todayIso()}
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:border-cyan-600"
                data-testid="bodycomp-date"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1 block">
                {language === 'IT' ? 'Sesso' : 'Sex'}<span className="text-red-400 ml-1">*</span>
              </label>
              <select
                value={sex} onChange={(e) => setSex(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:border-cyan-600"
                data-testid="bodycomp-sex"
              >
                <option value="M">M</option>
                <option value="F">F</option>
              </select>
            </div>
            <Field label={language === 'IT' ? 'Età' : 'Age'} value={age} onChange={setAge}
              testid="bodycomp-age" suffix="yr" placeholder="e.g. 25" required step="0.1" min={6} max={79} />
            <Field label={language === 'IT' ? 'Peso' : 'Weight'} value={weight} onChange={setWeight}
              testid="bodycomp-weight" suffix="kg" placeholder="e.g. 78.5" required step="0.1" min={1} max={299} />
          </div>

          {/* Method selector */}
          <div className="flex flex-wrap gap-2" data-testid="bodycomp-method-tabs">
            {METHODS.map((m) => (
              <Button
                key={m}
                size="sm"
                variant={method === m ? 'default' : 'outline'}
                onClick={() => setMethod(m)}
                data-testid={`method-${m}`}
                className={method === m
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  : 'border-zinc-700 text-zinc-300 hover:border-emerald-600 hover:text-emerald-300'}
              >
                {m}
              </Button>
            ))}
          </div>

          {/* SKINFOLDS */}
          {method === 'SKINFOLDS' && (
            <div className="space-y-4" data-testid="skinfold-panel">
              <div className="flex flex-wrap gap-2">
                {SKINFOLD_PROTOCOLS.map((p) => (
                  <Button
                    key={p.id} size="sm"
                    variant={protocol === p.id ? 'default' : 'outline'}
                    onClick={() => setProtocol(p.id)}
                    data-testid={`protocol-${p.id}`}
                    className={protocol === p.id
                      ? 'bg-cyan-600 hover:bg-cyan-500 text-white'
                      : 'border-zinc-700 text-zinc-300 hover:border-cyan-600 hover:text-cyan-300'}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>

              {protocol !== 'CUSTOM' && (
                <div className="bg-zinc-950/50 border border-zinc-800 rounded p-4">
                  <p className="text-xs text-zinc-500 mb-3">
                    {language === 'IT'
                      ? `Pliche obbligatorie (${sex}): ${requiredSkinfoldSites.join(', ')}`
                      : `Required sites (${sex}): ${requiredSkinfoldSites.join(', ')}`}
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {SKINFOLD_SITES.map((s) => {
                      const isRequired = requiredSkinfoldSites.includes(s);
                      return (
                        <Field
                          key={s}
                          label={s.charAt(0).toUpperCase() + s.slice(1)}
                          value={skinfolds[s]}
                          onChange={(v) => setSkinfolds({ ...skinfolds, [s]: v })}
                          testid={`skinfold-${s}`}
                          suffix="mm"
                          required={isRequired}
                          step="0.1" min={0} max={80}
                          placeholder={isRequired ? '' : '—'}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {protocol === 'CUSTOM' && (
                <div className="bg-zinc-950/50 border border-zinc-800 rounded p-4 space-y-3" data-testid="custom-skinfold-panel">
                  <p className="text-xs text-zinc-500">
                    {language === 'IT'
                      ? 'Aggiungi liberamente nome plica + valore (mm). Il BF% finale deve essere fornito manualmente.'
                      : 'Add free-form site name + value (mm). Final BF% must be entered manually below.'}
                  </p>
                  <div className="space-y-2">
                    {customSites.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded px-3 py-1.5 text-sm" data-testid={`custom-site-row-${i}`}>
                        <span className="text-zinc-300 flex-1">{s.name}</span>
                        <span className="text-zinc-400">{s.value_mm} mm</span>
                        <Button variant="ghost" size="sm" onClick={() => removeCustomSite(i)} data-testid={`custom-site-remove-${i}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text" value={customDraftName}
                      onChange={(e) => setCustomDraftName(e.target.value)}
                      placeholder={language === 'IT' ? 'Nome plica' : 'Site name'}
                      className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:border-cyan-600"
                      data-testid="custom-site-name-input"
                    />
                    <input
                      type="number" step="0.1" min={0} max={80} value={customDraftValue}
                      onChange={(e) => setCustomDraftValue(e.target.value)}
                      placeholder="mm"
                      className="w-24 bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-200 focus:outline-none focus:border-cyan-600"
                      data-testid="custom-site-value-input"
                    />
                    <Button type="button" size="sm" variant="outline" onClick={addCustomSite} data-testid="custom-site-add">
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="pt-3 border-t border-zinc-800">
                    <Field
                      label={language === 'IT' ? 'BF% manuale (obbligatorio)' : 'Manual BF% (required)'}
                      value={manual.body_fat_pct}
                      onChange={(v) => setManual({ ...manual, body_fat_pct: v })}
                      testid="custom-manual-bf"
                      suffix="%" required step="0.1" min={0} max={70}
                      placeholder="e.g. 14.2"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* BIA */}
          {method === 'BIA' && (
            <div className="bg-zinc-950/50 border border-zinc-800 rounded p-4 grid grid-cols-3 gap-3" data-testid="bia-panel">
              <Field label={language === 'IT' ? 'Massa grassa' : 'Fat mass'} value={bia.fm_kg}
                onChange={(v) => setBia({ ...bia, fm_kg: v })} testid="bia-fm" suffix="kg" step="0.1" min={0} max={199} />
              <Field label={language === 'IT' ? 'Massa magra (FFM)' : 'Lean mass (FFM)'} value={bia.ffm_kg}
                onChange={(v) => setBia({ ...bia, ffm_kg: v })} testid="bia-ffm" suffix="kg" step="0.1" min={0} max={199} />
              <Field label={language === 'IT' ? 'Massa muscolare' : 'Muscle mass'} value={bia.smm_kg}
                onChange={(v) => setBia({ ...bia, smm_kg: v })} testid="bia-smm" suffix="kg" step="0.1" min={0} max={199} />
              <Field label="TBW" value={bia.tbw_l} onChange={(v) => setBia({ ...bia, tbw_l: v })}
                testid="bia-tbw" suffix="L" step="0.1" min={0} max={119} />
              <Field label="ICW" value={bia.icw_l} onChange={(v) => setBia({ ...bia, icw_l: v })}
                testid="bia-icw" suffix="L" step="0.1" min={0} max={79} />
              <Field label="ECW" value={bia.ecw_l} onChange={(v) => setBia({ ...bia, ecw_l: v })}
                testid="bia-ecw" suffix="L" step="0.1" min={0} max={59} />
              <Field label={language === 'IT' ? 'Angolo di fase' : 'Phase angle'} value={bia.phase_angle_deg}
                onChange={(v) => setBia({ ...bia, phase_angle_deg: v })} testid="bia-phase" suffix="°" step="0.1" min={0} max={19} />
              <div className="col-span-3 text-xs text-zinc-500 italic">
                {language === 'IT' ? 'Servono almeno Massa grassa o Massa magra per il calcolo.' : 'At least fat or lean mass must be set for the calculation.'}
              </div>
            </div>
          )}

          {/* DEXA */}
          {method === 'DEXA' && (
            <div className="bg-zinc-950/50 border border-zinc-800 rounded p-4 grid grid-cols-3 gap-3" data-testid="dexa-panel">
              <Field label={language === 'IT' ? 'Massa grassa' : 'Fat mass'} value={dexa.fm_kg}
                onChange={(v) => setDexa({ ...dexa, fm_kg: v })} testid="dexa-fm" suffix="kg" required step="0.1" min={0} max={199} />
              <Field label={language === 'IT' ? 'Massa magra (FFM)' : 'Lean mass (FFM)'} value={dexa.ffm_kg}
                onChange={(v) => setDexa({ ...dexa, ffm_kg: v })} testid="dexa-ffm" suffix="kg" required step="0.1" min={0} max={199} />
              <Field label={language === 'IT' ? 'Massa ossea' : 'Bone mass'} value={dexa.bone_mineral_kg}
                onChange={(v) => setDexa({ ...dexa, bone_mineral_kg: v })} testid="dexa-bone" suffix="kg" step="0.01" min={0} max={19} />
              <div className="col-span-3 text-xs text-zinc-500 italic">
                {language === 'IT' ? 'Massa grassa e massa magra sono obbligatorie.' : 'Both fat and lean mass are required.'}
              </div>
            </div>
          )}

          {/* MANUAL */}
          {method === 'MANUAL' && (
            <div className="bg-zinc-950/50 border border-zinc-800 rounded p-4 grid grid-cols-3 gap-3" data-testid="manual-panel">
              <Field label="BF %" value={manual.body_fat_pct}
                onChange={(v) => setManual({ ...manual, body_fat_pct: v })} testid="manual-bf" suffix="%" step="0.1" min={0} max={70} />
              <Field label={language === 'IT' ? 'Massa grassa' : 'Fat mass'} value={manual.fat_mass_kg}
                onChange={(v) => setManual({ ...manual, fat_mass_kg: v })} testid="manual-fm" suffix="kg" step="0.1" min={0} max={199} />
              <Field label={language === 'IT' ? 'Massa magra' : 'Lean mass'} value={manual.lean_mass_kg}
                onChange={(v) => setManual({ ...manual, lean_mass_kg: v })} testid="manual-lean" suffix="kg" step="0.1" min={0} max={199} />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 mb-1 block">
              {language === 'IT' ? 'Note' : 'Notes'}
            </label>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder={language === 'IT' ? 'Condizioni del test, operatore…' : 'Test conditions, operator…'}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-600 resize-none"
              data-testid="bodycomp-notes"
            />
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="bodycomp-editor-error">
              {error}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-zinc-900 border-t border-zinc-800 px-6 py-3 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="bodycomp-editor-cancel">
            {language === 'IT' ? 'Annulla' : 'Cancel'}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="bodycomp-editor-save"
            className="bg-emerald-600 hover:bg-emerald-500 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {language === 'IT' ? 'Salva' : 'Save'}
          </Button>
        </footer>
      </div>
    </div>
  );
};

export default BodyCompositionEditorDialog;
